import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiTruck, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { veiculosAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { VeiculoCard, VeiculosFiltros, VeiculosPaginacao, ImageModal } from '../components/veiculos';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  padding: 0 ${theme.spacing[4]};

  @media (max-width: 768px) {
    gap: ${theme.spacing[4]};
    padding: 0 ${theme.spacing[3]};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};

  @media (max-width: 768px) {
    justify-content: stretch;
    margin-bottom: ${theme.spacing[3]};
  }
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${props => props.$disabled ? theme.colors.text.muted : theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  opacity: ${props => props.$disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    font-size: ${theme.fontSizes.sm};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};

  @media (max-width: 768px) {
    gap: ${theme.spacing[4]};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: ${theme.spacing[5]};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};

  @media (max-width: 768px) {
    padding: ${theme.spacing[8]} ${theme.spacing[4]};
  }
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;

  @media (max-width: 768px) {
    padding: ${theme.spacing[8]} ${theme.spacing[4]};
  }
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;

  @media (max-width: 768px) {
    padding: ${theme.spacing[8]} ${theme.spacing[4]};
  }
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const Veiculos = () => {
  const navigate = useNavigate();
  const [veiculos, setVeiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Carregar ordenação salva do localStorage
  const getOrdenacaoSalva = () => {
    try {
      const ordenacaoSalva = localStorage.getItem('veiculos_ordenacao');
      return ordenacaoSalva || '';
    } catch (error) {
      return '';
    }
  };

  const ordenacaoInicial = getOrdenacaoSalva();
  const [filtros, setFiltros] = useState({
    search: '',
    ano_min: '',
    ano_max: '',
    valor_min: '',
    valor_max: '',
    em_estoque: '',
    id_cliente: '',
    ordenacao: ordenacaoInicial, // Carregar ordenação salva
    page: 1,
    limit: 10
  });
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    images: []
  });

  // Estado para verificar se a checklist está completa
  const [checklistCompleta, setChecklistCompleta] = useState(false);

  // Cálculo de clientes únicos por ID (evitar duplicidades imprevistas)
  const uniqueClienteIds = Array.from(new Set((clientes || []).map(c => c?.id).filter(id => id != null)));

  // Verificar checklist do cliente específico do localStorage
  useEffect(() => {
    const verificarChecklist = () => {
      try {
        // Identificar o cliente atual (do filtro ou único cliente disponível)
        let clienteId = null;
        
        if (filtros.id_cliente) {
          clienteId = parseInt(filtros.id_cliente);
        } else if (uniqueClienteIds.length === 1) {
          clienteId = uniqueClienteIds[0];
        }

        if (!clienteId) {
          // Se não há cliente identificado, considerar como incompleta
          setChecklistCompleta(false);
          return;
        }

        // Buscar checklist do cliente específico
        const checklistSalva = localStorage.getItem(`checklist_configuracao_${clienteId}`);
        if (checklistSalva) {
          const checklist = JSON.parse(checklistSalva);
          const completa = checklist.temUsuario && 
                          checklist.temConfiguracoes && 
                          checklist.temInformacoesRevenda &&
                          checklist.temParametrosIA;
          setChecklistCompleta(completa);
        } else {
          // Se não há checklist salva para este cliente, considerar como incompleta
          setChecklistCompleta(false);
        }
      } catch (error) {
        console.error('Erro ao verificar checklist:', error);
        setChecklistCompleta(false);
      }
    };

    verificarChecklist();
    
    // Verificar periodicamente (a cada 5 segundos) para atualizar quando a checklist mudar
    const interval = setInterval(verificarChecklist, 5000);
    
    return () => clearInterval(interval);
  }, [filtros.id_cliente, uniqueClienteIds]);

  // Carregar clientes e aplicar filtro automático se necessário
  useEffect(() => {
    const carregarClientesEAplicarFiltro = async () => {
      try {
        setLoadingClientes(true);
        // Carregar clientes sempre para verificar quantidade
        const clientesResponse = await veiculosAPI.listarClientes();
        
        if (clientesResponse.data.success) {
          const clientesCarregados = clientesResponse.data.data || [];
          setClientes(clientesCarregados);

          // Determinar cliente único por ID (deduplicado)
          const uniqueIds = Array.from(new Set((clientesCarregados || []).map(c => c?.id).filter(id => id != null)));

          let filtrosParaBusca = filtros;
          if (uniqueIds.length === 1) {
            // Usuário tem apenas um cliente - aplicar filtro automaticamente
            const clienteId = uniqueIds[0].toString();
            filtrosParaBusca = {
              ...filtros,
              id_cliente: clienteId
            };
            setFiltros(filtrosParaBusca);
          } else {
            // Usuário tem múltiplos clientes - garantir que não há filtro de cliente aplicado
            filtrosParaBusca = {
              ...filtros,
              id_cliente: ''
            };
            setFiltros(filtrosParaBusca);
          }

          carregarVeiculos(filtrosParaBusca);
        } else {
          carregarVeiculos(filtros);
        }
      } catch (err) {
        // Em caso de erro, carregar veículos normalmente
        carregarVeiculos(filtros);
      } finally {
        setLoadingClientes(false);
      }
    };

    carregarClientesEAplicarFiltro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executar apenas uma vez na montagem

  // Verificar se deve mostrar filtro de cliente (com deduplicação por ID)
  const shouldShowClienteFilter = () => {
    // Se ainda está carregando clientes, não mostrar (para evitar flash)
    if (loadingClientes) return false;

    return uniqueClienteIds.length > 1;
  };

  // Carregar veículos
  const carregarVeiculos = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await veiculosAPI.listar(params);
      
      if (response.data.success) {
        setVeiculos(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar veículos');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };


  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    // Salvar ordenação no localStorage quando mudar
    if (novosFiltros.ordenacao !== undefined && novosFiltros.ordenacao !== filtros.ordenacao) {
      try {
        if (novosFiltros.ordenacao) {
          localStorage.setItem('veiculos_ordenacao', novosFiltros.ordenacao);
        } else {
          localStorage.removeItem('veiculos_ordenacao');
        }
      } catch (error) {
        // Silenciar erro ao salvar no localStorage
      }
    }

    setFiltros({
      ...novosFiltros,
      page: 1, // Reset para primeira página quando mudar filtros
      limit: filtros.limit || 10 // Manter o limite atual
    });
  };

  // Buscar com filtros (aceita parâmetros opcionais para usar filtros específicos)
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarVeiculos({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarVeiculos(filtrosParaBusca);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarVeiculos(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1 // Reset para primeira página quando mudar o limite
    };
    setFiltros(novosFiltros);
    carregarVeiculos(novosFiltros);
  };

  // Navegar para adicionar veículo
  const handleAdicionarVeiculo = () => {
    if (!checklistCompleta) {
      // Se a checklist não estiver completa, redirecionar para dashboard
      navigate('/dashboard/configuracoes');
      return;
    }
    navigate('/veiculos/novo');
  };

  // Abrir modal de imagens
  const handleImageClick = (images) => {
    setImageModal({
      isOpen: true,
      images: images
    });
  };

  // Fechar modal de imagens
  const handleCloseImageModal = () => {
    setImageModal({
      isOpen: false,
      images: []
    });
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando veículos...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar veículos</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarVeiculos()}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (veiculos.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiTruck />
          </EmptyIcon>
          <EmptyTitle>Nenhum veículo encontrado</EmptyTitle>
          <EmptyText>
            Não foram encontrados veículos com os filtros aplicados.
          </EmptyText>
          <EmptyAction onClick={handleAdicionarVeiculo}>
            <FiPlus />
            Adicionar Primeiro Veículo
          </EmptyAction>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {veiculos.map((veiculo) => (
            <VeiculoCard 
              key={veiculo.id} 
              veiculo={veiculo} 
              onImageClick={handleImageClick}
            />
          ))}
        </CardsGrid>

        
        {pagination.totalPages > 1 && (
          <VeiculosPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Veículos">
      <Container>
        <Header>
          <AddButton 
            onClick={handleAdicionarVeiculo}
            disabled={!checklistCompleta}
            $disabled={!checklistCompleta}
            title={!checklistCompleta ? 'Complete a checklist na Dashboard para adicionar veículos' : 'Adicionar novo veículo'}
          >
            <FiPlus />
            Adicionar Veículo
          </AddButton>
        </Header>

        <Content>
          {!loadingClientes && (
            <VeiculosFiltros
              filtros={filtros}
              onFiltrosChange={handleFiltrosChange}
              onBuscar={handleBuscar}
              loading={loading}
              showClienteFilter={shouldShowClienteFilter()}
              clientes={clientes}
            />
          )}

          {renderContent()}
        </Content>

        <ImageModal
          images={imageModal.images}
          isOpen={imageModal.isOpen}
          onClose={handleCloseImageModal}
        />
      </Container>
    </Layout>
  );
};

export default Veiculos;
